// /withdraw, the Send screen (docs/08-app-design.md 5.6): asset, amount,
// destination, then the shared review panel and confirm state machine
// (Swap.tsx is the pattern mirrored here: the focused task layout, loading
// the chain state and the signer, the review panel, the confirm state
// machine, the failure -> KeyRefresh path). A second, independent action
// sits behind its own tab, "Convert SOL" (unwrap, docs/01-architecture.md
// 7.6: an inner CloseAccount of the vault's wSOL account, its own Execute -
// it cannot share a signature with a native send, docs/01 6.2 step 8). Both
// sections stay mounted, the inactive one hidden, so a plan under review in
// one is never lost by looking at the other. A failure after either
// action's signature burns the key; the key refresh runs in place and both
// forms come back. A fee reserve refusal of either action keeps its
// InsufficientGas.movable beside its cost, so the review points to this
// device's older reserve when it can be moved (docs/18-one-fee-reserve.md
// section 6).

import { address } from '@solana/kit';
import { WSOL_MINT, addressBytes, ataAddress } from '@thevault/sdk';
import type { VaultSigner } from '@thevault/sdk';
import { TOKEN_PROGRAM } from '@thevault/sdk/canary';
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { KeyRefresh } from '../components/KeyRefresh';
import { Address, Amount, Button, Field, Notice, StatusLine } from '../components/primitives';
import { ReviewPanel } from '../components/Review';
import { Shell } from '../components/Shell';
import { SceneCard } from '../components/SceneWindow';
import { WalletAddressButton } from '../components/Wallet';
import { type ActionProgress, type ActionResult, type ChainState, type CostEstimate, type Engine, ActionFailed, ActionUnknown, InsufficientGas, type OlderReserve, RefreshRequired, describe, retirementOf } from '../engine';
import { type TokenAccountInfo, toBase58 } from '../rpc';
import { session, useSession } from '../session';
import { KNOWN, WSOL_MINT_B58, symbolOf } from '../tokens';
import { explorerBase, explorerUrl, parseUnits, shortSig } from '../util';

const TOKEN_PROGRAM_B58 = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022_B58 = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
const TOKEN_2022: Uint8Array = addressBytes(address(TOKEN_2022_B58));

interface Asset {
  key: string;
  kind: 'native' | 'token';
  mint?: string;
  tokenProgram?: Uint8Array;
  amount: bigint;
  decimals: number;
  label: string;
}

interface Assets {
  rentFloor: bigint;
  list: Asset[];
}

/** SOL, the vault's wSOL account, and every other token account the vault holds, across both token programs (docs/08 5.6). */
async function loadAssets(engine: Engine): Promise<Assets> {
  const { spendable, rentFloor } = await engine.vaultLamports();
  const list: Asset[] = [{ key: 'native', kind: 'native', amount: spendable, decimals: 9, label: 'SOL' }];
  const wsolAta = await ataAddress(engine.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM);
  list.push({ key: 'wsol', kind: 'token', mint: WSOL_MINT_B58, tokenProgram: TOKEN_PROGRAM, amount: await engine.tokenAmount(wsolAta), decimals: 9, label: 'SOL (wrapped)' });
  let rows: (TokenAccountInfo & { tokenProgram: Uint8Array })[] = [];
  try {
    const [a, b] = await Promise.all([engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_PROGRAM_B58), engine.rpc.getTokenAccountsByOwner(engine.info.address, TOKEN_2022_B58)]);
    rows = [...a.map((r) => ({ ...r, tokenProgram: TOKEN_PROGRAM })), ...b.map((r) => ({ ...r, tokenProgram: TOKEN_2022 }))];
  } catch {
    /* an RPC without the token index: only SOL and wSOL are offered */
  }
  for (const r of rows) {
    if (r.mint === WSOL_MINT_B58 || r.amount === 0n) continue;
    let decimals = KNOWN[r.mint]?.decimals ?? 0;
    if (!KNOWN[r.mint]) {
      try {
        decimals = await engine.decimalsOf(r.mint);
      } catch {
        /* raw units */
      }
    }
    list.push({ key: r.mint, kind: 'token', mint: r.mint, tokenProgram: r.tokenProgram, amount: r.amount, decimals, label: symbolOf(r.mint) });
  }
  return { rentFloor, list };
}

function assetSymbol(asset: Asset): string {
  return asset.kind === 'native' ? 'SOL' : symbolOf(asset.mint ?? '');
}

/** The monogram circle's classes: a known token gets its own tint (native SOL shares wrapped SOL's), anything else stays neutral. */
function glyphClass(asset: Asset): string {
  const known = KNOWN[asset.kind === 'native' ? WSOL_MINT_B58 : (asset.mint ?? '')];
  return known ? `token-glyph t-${known.symbol.toLowerCase()}` : 'token-glyph';
}

/** The two things this screen does; each has its own tab, its own section and its own review. */
type Tab = 'send' | 'convert';
const TAB_ORDER: Tab[] = ['send', 'convert'];
const TAB_IDS: Record<Tab, { tab: string; panel: string }> = {
  send: { tab: 'withdraw-tab', panel: 'withdraw-section' },
  convert: { tab: 'unwrap-tab', panel: 'unwrap-section' },
};

/** Plain-decimal text for a raw amount, with no floating point (mirrors Swap.tsx's own MAX helper). */
function formatPlain(raw: bigint, decimals: number): string {
  const s = raw.toString().padStart(decimals + 1, '0');
  const whole = s.slice(0, s.length - decimals);
  const frac = s.slice(s.length - decimals).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}

function TxLine({ signature, txBase }: { signature: string; txBase: string | null }) {
  return (
    <p className="ink">
      {explorerUrl(txBase, signature) ? (
        <>
          Transaction{' '}
          <a href={explorerUrl(txBase, signature)!} target="_blank" rel="noreferrer">
            {shortSig(signature)} ↗
          </a>
          .
        </>
      ) : (
        `Transaction ${shortSig(signature)}.`
      )}
    </p>
  );
}

interface WithdrawPlan {
  asset: Asset;
  amountRaw: bigint;
  destination: string;
  destinationBytes: Uint8Array;
}

interface UnwrapPlan {
  amount: bigint;
}

export function Withdraw() {
  const { settings } = useSession();
  const engine = session.engine();
  const [state, setState] = useState<ChainState | null>(null);
  const [signer, setSigner] = useState<VaultSigner | null>(null);
  const [assets, setAssets] = useState<Assets>({ rentFloor: 0n, list: [] });
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [refreshNeeded, setRefreshNeeded] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('send');

  // ---- withdraw form and review ----
  const [destination, setDestination] = useState('');
  const [assetKey, setAssetKey] = useState('native');
  const [amountText, setAmountText] = useState('');
  const [plan, setPlan] = useState<WithdrawPlan | null>(null);
  const [planFromAta, setPlanFromAta] = useState<string | null>(null);
  const [cost, setCost] = useState<CostEstimate | null>(null);
  // The refusal's older reserve reading when `cost` came from InsufficientGas (docs/18 section 6); undefined for an estimate.
  const [costMovable, setCostMovable] = useState<OlderReserve | null | undefined>(undefined);
  const [progress, setProgress] = useState<ActionProgress | null>(null);
  const [result, setResult] = useState<ActionResult | null>(null);
  const [unknownWithdraw, setUnknownWithdraw] = useState<{ signature: string; detail: string; note: string | null } | null>(null);
  const confirming = useRef(false);

  // ---- unwrap, its own action ----
  const [unwrapPlan, setUnwrapPlan] = useState<UnwrapPlan | null>(null);
  const [unwrapCost, setUnwrapCost] = useState<CostEstimate | null>(null);
  const [unwrapMovable, setUnwrapMovable] = useState<OlderReserve | null | undefined>(undefined);
  const [unwrapProgress, setUnwrapProgress] = useState<ActionProgress | null>(null);
  const [unwrapResult, setUnwrapResult] = useState<ActionResult | null>(null);
  const [unknownUnwrap, setUnknownUnwrap] = useState<{ signature: string; detail: string; note: string | null } | null>(null);
  const unwrapConfirming = useRef(false);

  const reload = useCallback(async () => {
    try {
      const s = await engine.readChain();
      setState(s);
      if (s.kind === 'vault') {
        const sg = await engine.signerFor(s);
        setSigner(sg);
        const keys = await engine.keyStatus(sg);
        // Records left unknown by an earlier attempt are checked before anything else (R45).
        if (keys.unresolved.length) await engine.reconcile(sg);
        setRefreshNeeded(keys.refreshReason);
      }
      setAssets(await loadAssets(engine));
    } catch (e) {
      setError(describe(e));
    }
  }, [engine]);
  useEffect(() => {
    void reload();
  }, [reload]);

  // The vault's own token account for whatever asset is under review, for the "From" row.
  useEffect(() => {
    let cancelled = false;
    if (plan && plan.asset.kind === 'token' && plan.asset.mint && plan.asset.tokenProgram) {
      void ataAddress(engine.vaultAddressBytes, addressBytes(address(plan.asset.mint)), plan.asset.tokenProgram).then((bytes) => {
        if (!cancelled) setPlanFromAta(toBase58(bytes));
      });
    } else {
      setPlanFromAta(null);
    }
    return () => {
      cancelled = true;
    };
  }, [plan, engine]);

  const selected = assets.list.find((a) => a.key === assetKey) ?? null;
  const destinationBytes = (() => {
    try {
      return addressBytes(address(destination.trim()));
    } catch {
      return null;
    }
  })();
  const amountRaw = selected ? parseUnits(amountText, selected.decimals) : null;
  const canReview = !!selected && !!destinationBytes && amountRaw !== null && amountRaw > 0n && amountRaw <= selected.amount && !!signer;

  /** What withdrawing `p` costs the fee reserve, rent for a destination token account included (R49); shared by the initial review and "check again" after a top-up. */
  const estimateWithdrawCost = (p: WithdrawPlan) => {
    setCost(null);
    setCostMovable(undefined);
    const estimate = p.asset.kind === 'native' ? engine.withdrawNativeCost() : engine.withdrawTokenCost(p.destinationBytes, addressBytes(address(p.asset.mint!)), p.asset.tokenProgram!);
    void estimate.then(setCost).catch((e) => setError(describe(e)));
  };

  const review = () => {
    if (!canReview || !selected || !destinationBytes) return;
    const p: WithdrawPlan = { asset: selected, amountRaw: amountRaw!, destination: destination.trim(), destinationBytes };
    setPlan(p);
    setProgress(null);
    setResult(null);
    setUnknownWithdraw(null);
    estimateWithdrawCost(p);
  };

  const confirmWithdraw = async () => {
    if (!plan || !signer || confirming.current) return;
    confirming.current = true;
    setFailed(null);
    setUnknownWithdraw(null);
    try {
      const r =
        plan.asset.kind === 'native'
          ? await engine.withdrawNative(signer, plan.destinationBytes, plan.amountRaw, setProgress)
          : await engine.withdrawToken(signer, plan.destinationBytes, addressBytes(address(plan.asset.mint!)), plan.asset.tokenProgram!, plan.amountRaw, plan.asset.decimals, setProgress);
      setResult(r);
      await reload();
    } catch (err) {
      // A burned key runs the refresh; a refusal before any key existed (the program check, a stale slot, the fee reserve) only reports.
      if (err instanceof RefreshRequired) {
        setProgress(null);
        setRefreshNeeded(err.reason);
      } else if (err instanceof ActionFailed) setFailed(`${err.message} ${err.detail}`);
      else if (err instanceof ActionUnknown) setUnknownWithdraw({ signature: err.signature, detail: err.detail, note: null });
      else if (err instanceof InsufficientGas) {
        setProgress(null);
        setCost(err.cost);
        setCostMovable(err.movable);
      } else setProgress({ step: 'rejected', error: describe(err) });
    } finally {
      confirming.current = false;
    }
  };

  /** An unknown withdrawal outcome, checked against the chain (R45): landed, failed, or still unknown. */
  const checkUnknownWithdraw = async () => {
    if (!unknownWithdraw || !signer) return;
    try {
      await engine.reconcile(signer);
      const record = (await engine.history(signer)).find((r) => r.txSignature === unknownWithdraw.signature);
      if (record?.status === 'landed') {
        setResult({ signature: unknownWithdraw.signature, leaf: record.leaf, transactionSlot: 0, readback: null });
        setProgress({ step: 'landed', leaf: record.leaf, signature: unknownWithdraw.signature });
        setUnknownWithdraw(null);
        await reload();
      } else if (record?.status === 'burned') {
        // The ledger's reason decides the words (R56): only a confirmed error is "failed on chain"; an unconfirmed expiry is "expired".
        const retired = retirementOf(record);
        setUnknownWithdraw(null);
        setProgress({ step: retired.step, leaf: record.leaf, signature: unknownWithdraw.signature, error: retired.sentence });
        setFailed(retired.sentence);
      } else {
        setUnknownWithdraw({ ...unknownWithdraw, note: 'Still unknown: the network has no confirmation yet. Check again in a moment.' });
      }
    } catch (e) {
      setUnknownWithdraw({ ...unknownWithdraw, note: describe(e) });
    }
  };

  const resetWithdraw = () => {
    setPlan(null);
    setCost(null);
    setCostMovable(undefined);
    setProgress(null);
    setResult(null);
    setUnknownWithdraw(null);
    setAmountText('');
  };

  const wsolAsset = assets.list.find((a) => a.key === 'wsol') ?? null;

  /** What unwrapping costs the fee reserve (R49); shared by the initial review and "check again". */
  const estimateUnwrapCost = () => {
    setUnwrapCost(null);
    setUnwrapMovable(undefined);
    void engine.unwrapCost().then(setUnwrapCost).catch((e) => setError(describe(e)));
  };

  const reviewUnwrap = () => {
    if (!wsolAsset || wsolAsset.amount <= 0n || !signer) return;
    setUnwrapPlan({ amount: wsolAsset.amount });
    setUnwrapProgress(null);
    setUnwrapResult(null);
    setUnknownUnwrap(null);
    estimateUnwrapCost();
  };

  const confirmUnwrap = async () => {
    if (!unwrapPlan || !signer || unwrapConfirming.current) return;
    unwrapConfirming.current = true;
    setFailed(null);
    setUnknownUnwrap(null);
    try {
      const r = await engine.unwrap(signer, setUnwrapProgress);
      setUnwrapResult(r);
      await reload();
    } catch (err) {
      if (err instanceof RefreshRequired) {
        setUnwrapProgress(null);
        setRefreshNeeded(err.reason);
      } else if (err instanceof ActionFailed) setFailed(`${err.message} ${err.detail}`);
      else if (err instanceof ActionUnknown) setUnknownUnwrap({ signature: err.signature, detail: err.detail, note: null });
      else if (err instanceof InsufficientGas) {
        setUnwrapProgress(null);
        setUnwrapCost(err.cost);
        setUnwrapMovable(err.movable);
      } else setUnwrapProgress({ step: 'rejected', error: describe(err) });
    } finally {
      unwrapConfirming.current = false;
    }
  };

  /** An unknown unwrap outcome, checked against the chain (R45): landed, failed, or still unknown. */
  const checkUnknownUnwrap = async () => {
    if (!unknownUnwrap || !signer) return;
    try {
      await engine.reconcile(signer);
      const record = (await engine.history(signer)).find((r) => r.txSignature === unknownUnwrap.signature);
      if (record?.status === 'landed') {
        setUnwrapResult({ signature: unknownUnwrap.signature, leaf: record.leaf, transactionSlot: 0, readback: null });
        setUnwrapProgress({ step: 'landed', leaf: record.leaf, signature: unknownUnwrap.signature });
        setUnknownUnwrap(null);
        await reload();
      } else if (record?.status === 'burned') {
        // The ledger's reason decides the words (R56): only a confirmed error is "failed on chain"; an unconfirmed expiry is "expired".
        const retired = retirementOf(record);
        setUnknownUnwrap(null);
        setUnwrapProgress({ step: retired.step, leaf: record.leaf, signature: unknownUnwrap.signature, error: retired.sentence });
        setFailed(retired.sentence);
      } else {
        setUnknownUnwrap({ ...unknownUnwrap, note: 'Still unknown: the network has no confirmation yet. Check again in a moment.' });
      }
    } catch (e) {
      setUnknownUnwrap({ ...unknownUnwrap, note: describe(e) });
    }
  };

  const resetUnwrap = () => {
    setUnwrapPlan(null);
    setUnwrapCost(null);
    setUnwrapMovable(undefined);
    setUnwrapProgress(null);
    setUnknownUnwrap(null);
    setUnwrapResult(null);
  };

  const resetAll = () => {
    // A recovery just completed (R54): the failure state that gated the
    // forms away must clear too, or they never come back.
    setFailed(null);
    resetWithdraw();
    resetUnwrap();
  };

  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const keyLine = signer ? `tree ${signer.treeIndex}` : 'this tree';
  const detail = state?.kind === 'vault' ? `KEY TREE ${state.snap.treeIndex}` : state ? state.kind.toUpperCase() : 'READING';
  const vaultReady = state?.kind === 'vault';
  // The forms wait while a key refresh or a recovery runs, then come back in place.
  const formsShown = vaultReady && !failed && !refreshNeeded;
  const reviewing = tab === 'send' ? !!plan : !!unwrapPlan;

  /** Arrow keys, Home and End move between the two tabs (the tab pattern of the ARIA authoring practices). */
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = TAB_ORDER.indexOf(tab);
    let next: Tab | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = TAB_ORDER[(at + 1) % TAB_ORDER.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = TAB_ORDER[(at + TAB_ORDER.length - 1) % TAB_ORDER.length];
    else if (e.key === 'Home') next = TAB_ORDER[0];
    else if (e.key === 'End') next = TAB_ORDER[TAB_ORDER.length - 1];
    if (!next) return;
    e.preventDefault();
    setTab(next);
    document.getElementById(TAB_IDS[next].tab)?.focus();
  };

  return (
    <Shell number="04" title="Send" detail={detail}>
      <div className={vaultReady ? 'task' : 'task single'}>
        <div className="task-main">
          <div className="composer-head">
            <h1>Send assets</h1>
            <span className="dim">{formsShown && reviewing ? 'Review' : 'From your vault'}</span>
          </div>
          <p className="task-intro">Assets sent to another wallet are then controlled by that wallet's key, not by the vault.</p>

          {error ? (
            <StatusLine state="failed" word="Could not read the chain.">
              {error}
            </StatusLine>
          ) : null}
          {state && state.kind !== 'vault' ? (
            <Notice label="Not available">
              <p className="ink">{state.kind === 'absent' || state.kind === 'prefunded' ? 'Activate the vault from the overview before sending.' : 'The vault is not in a state that can send.'}</p>
            </Notice>
          ) : null}

          {refreshNeeded && signer ? (
            <Notice label="Key refresh" tone="warn">
              <KeyRefresh
                engine={engine}
                signer={signer}
                reason={`${refreshNeeded} It runs now, before anything else; the forms follow.`}
                onDone={() => {
                  setRefreshNeeded(null);
                  resetAll();
                  void reload();
                }}
              />
            </Notice>
          ) : null}

          {failed && signer ? (
            <Notice label="Key refresh" tone="warn">
              <KeyRefresh
                engine={engine}
                signer={signer}
                reason={`${failed} The vault refreshes its key now, then the forms come back.`}
                onDone={() => {
                  resetAll();
                  void reload();
                }}
              />
            </Notice>
          ) : null}

          {formsShown ? (
            <>
              <div>
                <div className="segmented" role="tablist" aria-label="Send or convert" onKeyDown={onTabKey}>
                  {TAB_ORDER.map((t) => (
                    <button key={t} type="button" role="tab" id={TAB_IDS[t].tab} aria-selected={tab === t} aria-controls={TAB_IDS[t].panel} tabIndex={tab === t ? 0 : -1} onClick={() => setTab(t)}>
                      {t === 'send' ? 'Send' : 'Convert SOL'}
                    </button>
                  ))}
                </div>
              </div>

              <div id="withdraw-section" className="stack" role="tabpanel" aria-labelledby={TAB_IDS.send.tab} hidden={tab !== 'send'}>
                {!plan ? (
                  <form
                    className="composer"
                    onSubmit={(e) => {
                      e.preventDefault();
                      review();
                    }}
                  >
                    <Field id="withdraw-asset" label="Asset">
                      <select id="withdraw-asset" className="input" value={assetKey} onChange={(e) => setAssetKey(e.target.value)}>
                        {assets.list.map((a) => (
                          <option key={a.key} value={a.key}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      id="withdraw-amount"
                      label="Amount"
                      help={
                        selected ? (
                          selected.kind === 'native' ? (
                            <>
                              Up to <Amount raw={selected.amount} decimals={9} symbol="SOL" />. Another <Amount raw={assets.rentFloor} decimals={9} symbol="SOL" /> is kept to hold the account open and stays in the vault.
                            </>
                          ) : (
                            <>
                              Available: <Amount raw={selected.amount} decimals={selected.decimals} symbol={assetSymbol(selected)} />
                            </>
                          )
                        ) : undefined
                      }
                    >
                      <div className="field-row">
                        <input id="withdraw-amount" className="input" inputMode="decimal" value={amountText} onChange={(e) => setAmountText(e.target.value)} placeholder="0.00" autoComplete="off" />
                        <Button variant="ghost" small type="button" disabled={!selected} onClick={() => selected && setAmountText(formatPlain(selected.amount, selected.decimals))}>
                          Max
                        </Button>
                      </div>
                    </Field>
                    <Field id="withdraw-destination" label="Destination address" help={destination.trim().length > 0 && !destinationBytes ? 'Not a valid Solana address.' : undefined}>
                      <input id="withdraw-destination" className="input" value={destination} onChange={(e) => setDestination(e.target.value)} autoComplete="off" spellCheck={false} placeholder="Solana address" />
                    </Field>
                    <WalletAddressButton onAddress={setDestination} />
                    <Button type="submit" block disabled={!canReview}>
                      Review send
                    </Button>
                  </form>
                ) : null}

                {plan ? (
                  <ReviewPanel
                    engine={engine}
                    title="Review your send"
                    rpcUrl={settings.rpcUrl}
                    keyLine={keyLine}
                    cost={cost}
                    movable={costMovable}
                    progress={progress}
                    confirmLabel="Confirm send"
                    onConfirm={() => void confirmWithdraw()}
                    onCancel={result || unknownWithdraw ? undefined : resetWithdraw}
                    onRecheck={() => estimateWithdrawCost(plan)}
                    disabled={!signer || !!unknownWithdraw}
                    net={{ label: 'Send', value: <Amount raw={plan.amountRaw} decimals={plan.asset.decimals} symbol={assetSymbol(plan.asset)} size="large" /> }}
                    rows={[
                      { group: 'What you send', label: 'To', value: <Address value={plan.destination} kind="wallet" full />, wide: true },
                      { group: 'What you send', label: 'From', value: plan.asset.kind === 'native' ? "The vault's SOL, above its rent floor." : planFromAta ? <Address value={planFromAta} kind="token account" /> : "The vault's token account." },
                    ]}
                  >
                    {unknownWithdraw ? (
                      <Notice label="Outcome unknown" tone="warn">
                        <p className="ink">{unknownWithdraw.note ?? `${unknownWithdraw.detail} Nothing is retired and nothing new is signed until the outcome is known.`}</p>
                        <div className="actions">
                          <Button small onClick={() => void checkUnknownWithdraw()}>
                            Check again
                          </Button>
                        </div>
                      </Notice>
                    ) : null}
                    {result ? (
                      <Notice label="Landed" tone="accent">
                        <TxLine signature={result.signature} txBase={txBase} />
                        <div className="actions">
                          <Button small onClick={resetWithdraw}>
                            Send again
                          </Button>
                        </div>
                      </Notice>
                    ) : null}
                  </ReviewPanel>
                ) : null}
              </div>

              <div id="unwrap-section" className="stack" role="tabpanel" aria-labelledby={TAB_IDS.convert.tab} hidden={tab !== 'convert'}>
                {!unwrapPlan ? (
                  <div className="composer">
                    <p className="ink">Wrapped SOL is the form of SOL that swaps use. Converting turns it back into ordinary SOL in the vault.</p>
                    <p className="swap-amount ink">
                      Wrapped SOL available:{' '}
                      <strong>
                        <Amount raw={wsolAsset?.amount ?? 0n} decimals={9} symbol="SOL" />
                      </strong>
                    </p>
                    <p>Converting and sending are separate actions. Convert first, then send the ordinary SOL from the Send tab.</p>
                    {wsolAsset && wsolAsset.amount <= 0n ? <p>Nothing to convert yet.</p> : null}
                    <Button type="button" block disabled={!wsolAsset || wsolAsset.amount <= 0n || !signer} onClick={reviewUnwrap}>
                      Review conversion
                    </Button>
                  </div>
                ) : null}

                {unwrapPlan ? (
                  <ReviewPanel
                    engine={engine}
                    title="Review your conversion"
                    rpcUrl={settings.rpcUrl}
                    keyLine={keyLine}
                    cost={unwrapCost}
                    movable={unwrapMovable}
                    progress={unwrapProgress}
                    confirmLabel="Confirm conversion"
                    onConfirm={() => void confirmUnwrap()}
                    onCancel={unwrapResult || unknownUnwrap ? undefined : resetUnwrap}
                    onRecheck={estimateUnwrapCost}
                    disabled={!signer || !!unknownUnwrap}
                    net={{ label: 'Convert', value: <Amount raw={unwrapPlan.amount} decimals={9} symbol="SOL" size="large" /> }}
                    rows={[{ group: 'What you convert', label: 'To', value: <Address value={engine.info.address} kind="vault" />, detail: 'Ordinary SOL in the same vault; nothing leaves it.' }]}
                  >
                    {unknownUnwrap ? (
                      <Notice label="Outcome unknown" tone="warn">
                        <p className="ink">{unknownUnwrap.note ?? `${unknownUnwrap.detail} Nothing is retired and nothing new is signed until the outcome is known.`}</p>
                        <div className="actions">
                          <Button small onClick={() => void checkUnknownUnwrap()}>
                            Check again
                          </Button>
                        </div>
                      </Notice>
                    ) : null}
                    {unwrapResult ? (
                      <Notice label="Landed" tone="accent">
                        <TxLine signature={unwrapResult.signature} txBase={txBase} />
                        <div className="actions">
                          <Button small onClick={resetUnwrap}>
                            Convert again
                          </Button>
                        </div>
                      </Notice>
                    ) : null}
                  </ReviewPanel>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        {vaultReady ? (
          <aside className="task-side" aria-label="What you can send">
            <SceneCard scene="send" />
            <div className="panel">
              <span className="eyebrow">In your vault</span>
              {assets.list.length ? (
                assets.list.map((a) => (
                  <div className="side-row" key={a.key}>
                    <span>
                      <span className={glyphClass(a)} aria-hidden="true">
                        {assetSymbol(a).slice(0, 1)}
                      </span>
                      {a.label}
                    </span>
                    <Amount raw={a.amount} decimals={a.decimals} symbol={assetSymbol(a)} maxFraction={Math.min(a.decimals, 4)} />
                  </div>
                ))
              ) : (
                <p className="activity-note">Reading your vault…</p>
              )}
              <p className="activity-note">SOL here is what can be sent. A small amount is always kept to hold the account open.</p>
            </div>
            <div className="panel">
              <span className="eyebrow">What it costs</span>
              <p>There is no protocol fee on sends.</p>
              <p>The network fee comes from your fee reserve. It is shown in the review before you confirm.</p>
            </div>
          </aside>
        ) : null}
      </div>
    </Shell>
  );
}
